import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  getWaitList,
  type WaitListEntry,
} from 'restaurantAdmin/reservations/service/sidePanel/waitList/apiHelpers';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useServerSentEvents } from '@shared/hooks/useServerSentEvents';
import type { ReactNode } from 'react';
import { createContext, useMemo, useState } from 'react';
import { seatWaitListEntry } from 'restaurantAdmin/reservations/apiHelpers';
import { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { ApiError } from '@shared/api/errors';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import { useReservationServiceContext } from 'restaurantAdmin/reservations/service/state/ReservationServiceContext';
import { useServiceSearchParams } from 'restaurantAdmin/reservations/service/hooks/useServiceSearchParams';

export interface WaitListContextState {
  waitListEntries: WaitListEntry[];
  selectedWaitListEntry: WaitListEntry | undefined;
  fetchWaitListEntries: () => void;
  setSelectedWaitListEntryIndex: (index: number) => void;
  confirmSeatWaitListEntry: (
    floorPlanTables: HostFloorPlanTable[],
  ) => Promise<void>;
}

export const WaitListContext = createContext<WaitListContextState | null>(null);

export const useWaitListContext = (): WaitListContextState =>
  useDefinedContext(WaitListContext);

export const WaitListContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { id: restaurantId } = useRestaurant();

  const [selectedWaitListEntryIndex, setSelectedWaitListEntryIndex] =
    useState<number>(-1);

  const { data: waitListEntries = [], rerun: fetchWaitListEntries } =
    useAbortEffect(() => getWaitList({ restaurantId }), [restaurantId]);

  useServerSentEvents(restaurantId, () => {
    fetchWaitListEntries();
  });

  const selectedWaitListEntry = waitListEntries[selectedWaitListEntryIndex];

  const { closeSidePanelSheet } = useReservationServiceContext();
  const { exitSeatMode } = useServiceSearchParams();

  const confirmSeatWaitListEntry = async (
    floorPlanTables: HostFloorPlanTable[],
  ) => {
    try {
      await seatWaitListEntry({
        restaurantId,
        floorPlanTableIds: floorPlanTables.map((t) => t.id),
        waitListEntryId: selectedWaitListEntry.id,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to seat wait list entry. Please try again.',
        });
      }
      reportAppError(error);
    }
    closeSidePanelSheet();
    setSelectedWaitListEntryIndex(-1);
    exitSeatMode();
    fetchWaitListEntries();
  };

  const value = useMemo(
    () => ({
      waitListEntries,
      setSelectedWaitListEntryIndex,
      fetchWaitListEntries,
      selectedWaitListEntry,
      confirmSeatWaitListEntry,
    }),
    [waitListEntries, selectedWaitListEntryIndex],
  );

  return (
    <WaitListContext.Provider value={value}>
      {children}
    </WaitListContext.Provider>
  );
};
